import { useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";

const DIVISION_PRESETS = {
  "6-9": { round: 60, rest: 30 },
  "10-11": { round: 60, rest: 30 },
  "12-13": { round: 90, rest: 45 },
  "14-15": { round: 90, rest: 45 },
  "16-17": { round: 120, rest: 60 },
  "18-40": { round: 120, rest: 60 },
};

export default function UpcomingFightsPage() {
  const [fights, setFights] = useState({ A: [], B: [] });
  const [currentIndex, setCurrentIndex] = useState({ A: null, B: null });
  const [timers, setTimers] = useState({ A: 60, B: 60 });
  const [phase, setPhase] = useState({ A: "round", B: "round" });

  const socketRef = useRef(null);
  const editTimeouts = useRef({});
  const lastServerTime = useRef({ A: 0, B: 0 });
  const lastUpdateTimestamp = useRef({ A: 0, B: 0 });

  // ---------------- SINGLE RING PARAMETER ----------------
  const params = new URLSearchParams(window.location.search);
  const visibleRing = params.get("ring")?.toUpperCase(); // "A" or "B"

  // ---------------- SOCKET + INITIAL DATA ----------------
  useEffect(() => {
    async function fetchAllFights() {
      ["A", "B"].forEach(async (ring) => {
        const res = await fetch(`/api/fights?ring=${ring}`);
        const data = await res.json();
        setFights((prev) => ({ ...prev, [ring]: data }));
      });
    }
    fetchAllFights();
  }, []);

  useEffect(() => {
    const socket = io("https://warfc.com", {
      path: "/socket.io",
      transports: ["websocket"],
    });
    socketRef.current = socket;

    ["A", "B"].forEach((ring) => socket.emit("join_ring", ring));

    // --- Judging State ---
    socket.on("judging_state", (data) => {
      setFights((prevFights) => {
        const newFights = { ...prevFights };
        const newIndex = { ...currentIndex };
        const newPhase = { ...phase };

        Object.entries(data).forEach(([ring, state]) => {
          if (!state) return;

          // Sync timer and phase from server
          lastServerTime.current[ring] = state.timer ?? lastServerTime.current[ring];
          lastUpdateTimestamp.current[ring] = Date.now();
          setTimers((prev) => ({ ...prev, [ring]: state.timer ?? prev[ring] }));
          newPhase[ring] = state.phase ?? "round";

          socketRef.current.timerRunning = socketRef.current.timerRunning || {};
          socketRef.current.timerRunning[ring] = state.timerRunning ?? false;

          const incomingFights = state.allFights || state.upcomingFights || [];
          incomingFights.forEach((f) => {
            const idx = newFights[ring].findIndex((existing) => existing.id === f.id);
            if (idx >= 0) newFights[ring][idx] = f;
            else newFights[ring].push(f);
          });
          newFights[ring].sort((a, b) => (a.position ?? a.id) - (b.position ?? b.id));

          newIndex[ring] = newFights[ring].findIndex((f) => f.id === state.currentFight?.id) ?? null;
        });

        setCurrentIndex(newIndex);
        setPhase(newPhase);

        return newFights;
      });
    });

    // --- Timer Updates ---
    socket.on("timer_update", ({ ring, timer, phase: p }) => {
      setTimers((prev) => ({ ...prev, [ring]: timer }));
      setPhase((prev) => ({ ...prev, [ring]: p }));
      lastServerTime.current[ring] = timer;
      lastUpdateTimestamp.current[ring] = Date.now();
    });

    // --- Export CSV ---
    socket.on("completed_fights_export", (csv) => {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "completed_fights.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });

    return () => socket.disconnect();
  }, []);

  // ---------------- SMOOTH TIMER COUNTDOWN ----------------
  useEffect(() => {
    const interval = setInterval(() => {
      ["A", "B"].forEach((ring) => {
        setTimers((prev) => {
          const elapsed = Math.floor((Date.now() - lastUpdateTimestamp.current[ring]) / 1000);
          if (!socketRef.current?.timerRunning?.[ring]) return prev;
          const newTime = Math.max(0, lastServerTime.current[ring] - elapsed);
          return { ...prev, [ring]: newTime };
        });
      });
    }, 250);
    return () => clearInterval(interval);
  }, []);

  // ---------------- TIMER CONTROLS ----------------
  const startTimer = (ring) => {
    socketRef.current?.emit("timer_start", { ring });
    lastServerTime.current[ring] = timers[ring];
    lastUpdateTimestamp.current[ring] = Date.now();
  };
  const pauseTimer = (ring) => {
    socketRef.current?.emit("timer_pause", { ring });
  };

  // ---------------- FIGHT SELECTION ----------------
  const selectFight = (ring, index) => {
    const fight = fights[ring][index];
    if (!fight) return;

    setCurrentIndex((prev) => ({ ...prev, [ring]: index }));

    const preset = DIVISION_PRESETS[fight.division] || { round: 120, rest: 60 };
    setTimers((prev) => ({ ...prev, [ring]: preset.round }));
    setPhase((prev) => ({ ...prev, [ring]: "round" }));
    lastServerTime.current[ring] = preset.round;
    lastUpdateTimestamp.current[ring] = Date.now();

    socketRef.current?.emit("set_current_fight", { ring, id: fight.id });
  };

  // ---------------- EDIT / ADD ----------------
  const editFight = (ring, index, field, value) => {
    setFights((prev) => {
      const updated = [...prev[ring]];
      updated[index] = { ...updated[index], [field]: value };

      const fightId = updated[index]?.id;
      if (!fightId) return { ...prev, [ring]: updated };

      const key = `${ring}-${fightId}-${field}`;
      if (editTimeouts.current[key]) clearTimeout(editTimeouts.current[key]);

      editTimeouts.current[key] = setTimeout(() => {
        socketRef.current?.emit("edit_fight", { ring, id: fightId, field, value });

        if (field === "division") {
          const preset = DIVISION_PRESETS[value];
          if (preset) {
            socketRef.current?.emit("set_time_by_division", {
              ring,
              roundDuration: preset.round,
              restDuration: preset.rest,
            });
            setTimers((prev) => ({ ...prev, [ring]: preset.round }));
            setPhase((prev) => ({ ...prev, [ring]: "round" }));
            lastServerTime.current[ring] = preset.round;
            lastUpdateTimestamp.current[ring] = Date.now();
          }
        }
      }, 300);

      return { ...prev, [ring]: updated };
    });
  };

  const addFight = (ring) => {
    const newFight = {
      id: `temp-${Date.now()}`,
      red: "",
      blue: "",
      division: "",
      weight: "",
      status: "upcoming",
      number: fights[ring].length + 1,
    };
    setFights((prev) => ({ ...prev, [ring]: [...prev[ring], newFight] }));
    socketRef.current?.emit("add_fight", { ring });
  };

  const exportExcel = () => {
    ["A", "B"].forEach((ring) => {
      const fightsToExport = fights[ring];
      if (fightsToExport.length === 0) return;

      const headers = ["#", "Red", "Blue", "Division", "Weight", "Status"];
      const rows = fightsToExport.map((f, i) => [
        i + 1,
        f.red,
        f.blue,
        f.division,
        f.weight,
        f.status,
      ]);

      const csvContent =
        [headers, ...rows]
          .map((row) => row.map((v) => `"${v ?? ""}"`).join(","))
          .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `fights_ring_${ring}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  // ---------------- RENDER TABLE ----------------
  const renderTable = (ring, isSingle) => {
  return (
    <div
      className={`p-4 bg-gray-900 rounded-xl shadow-2xl border border-gray-700
        ${isSingle ? "w-full" : "w-full md:w-1/2"}
      `}
    >
      <div className="flex justify-center items-center space-x-3 mb-6">
        <button
          onClick={() => startTimer(ring)}
          className="px-5 py-2 bg-green-600 hover:bg-green-700 transition duration-200 rounded-lg font-bold shadow-lg"
        >
          Start
        </button>
        <button
          onClick={() => pauseTimer(ring)}
          className="px-5 py-2 bg-yellow-600 hover:bg-yellow-700 transition duration-200 rounded-lg font-bold shadow-lg"
        >
          Pause
        </button>
        <span className="ml-4 text-xl font-extrabold text-white">
          {Math.floor(timers[ring] / 60)}:
          {String(timers[ring] % 60).padStart(2, "0")} (
          {phase[ring].toUpperCase()})
        </span>
        <button
          onClick={() => {
            const idx = currentIndex[ring];
            if (idx === null) return;
            const fight = fights[ring][idx];
            const preset = DIVISION_PRESETS[fight.division] || { round: 120 };
            setTimers((prev) => ({ ...prev, [ring]: preset.round }));
            setPhase((prev) => ({ ...prev, [ring]: "round" }));
            lastServerTime.current[ring] = preset.round;
            lastUpdateTimestamp.current[ring] = Date.now();
          }}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 transition duration-200 rounded-lg font-bold shadow-lg"
        >
          Reset
        </button>
      </div>

      {/* Header with Logo */}
      <div className="flex items-center justify-center mb-4">
        <img
          src="https://i.ibb.co/jPxWrtSv/war11.png"
          alt="Event Logo"
          className="w-auto h-12 mr-3"
        />
        <h2 className="text-3xl font-extrabold text-white tracking-wider">
          Ring {ring} Schedule
        </h2>
      </div>

      {/* Fights Table */}
      <div className="max-h-[750px] overflow-y-auto rounded-lg border border-gray-700">
        <table className="w-full text-center border-collapse">
          <thead>
            <tr>
              <th className="sticky top-0 z-10 bg-gray-800 p-3 border-b border-gray-700">#</th>
              <th className="sticky top-0 z-10 bg-red-600 p-3 border-b border-gray-700">Red</th>
              <th className="sticky top-0 z-1 bg-gray-800 p-3 border-b border-gray-700">Division / Weight</th>
              <th className="sticky top-0 z-10 bg-blue-600 p-3 border-b border-gray-700">Blue</th>
              <th className="sticky top-0 z-10 bg-gray-800 p-3 border-b border-gray-700">Select</th>
            </tr>
          </thead>

          <tbody>
            {fights[ring].length === 0 && (
              <tr>
                <td colSpan={5} className="p-3 text-gray-400">
                  No fights yet
                </td>
              </tr>
            )}

            {fights[ring].map((f, i) => (
              <tr
                key={f.id || i}
                className={`border-b border-gray-700 transition ${
                  i === currentIndex[ring] ? "bg-yellow-600/40" : ""
                } ${f.status === "completed" ? "opacity-50" : "hover:bg-gray-700/50"}`}
              >
                <td className="p-2 font-semibold">{i + 1}</td>

                <td className="p-2">
                  <input
                    value={f.red || ""}
                    disabled={f.status === "completed"}
                    onChange={(e) => editFight(ring, i, "red", e.target.value)}
                    className="w-full text-center rounded px-2 py-1 text-white font-semibold bg-red-800"
                  />
                </td>

                <td className="p-2 flex justify-center space-x-1">
                  <select
                    value={f.division || ""}
                    disabled={f.status === "completed"}
                    onChange={(e) => editFight(ring, i, "division", e.target.value)}
                    className="bg-gray-800 text-white rounded px-2 py-1 text-center"
                  >
                    <option value="">Select</option>
                    {Object.keys(DIVISION_PRESETS).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>

                  <input
                    value={f.weight || ""}
                    disabled={f.status === "completed"}
                    onChange={(e) => editFight(ring, i, "weight", e.target.value)}
                    placeholder="kg"
                    className="bg-gray-800 text-white rounded px-2 py-1 text-center"
                    style={{ width: "4.5rem" }}
                  />
                </td>

                <td className="p-2">
                  <input
                    value={f.blue || ""}
                    disabled={f.status === "completed"}
                    onChange={(e) => editFight(ring, i, "blue", e.target.value)}
                    className="w-full text-center rounded px-2 py-1 text-white font-semibold bg-blue-800"
                  />
                </td>

                <td className="p-2 flex flex-col space-y-1">
                  <button
                    onClick={() => selectFight(ring, i)}
                    disabled={f.status === "completed"}
                    className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 rounded font-bold text-sm"
                  >
                    Select
                  </button>
                </td>
              </tr>
            ))}

            {/* Add fight at the end */}
            <tr>
              <td colSpan={5} className="p-3 text-center">
                <button
                  onClick={() => addFight(ring)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-bold"
                >
                  + Add Fight
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
  // ---------------- MAIN RENDER ----------------
  return (
  <div className="w-screen min-h-screen flex bg-gray-900 text-white p-2 md:p-4">
    {visibleRing ? (
      renderTable(visibleRing, true)
    ) : (
      <>
        {renderTable("A", false)}
        {renderTable("B", false)}
      </>
    )}

    <button
      onClick={exportExcel}
      className="fixed bottom-6 right-6 px-6 py-3 bg-purple-700 hover:bg-purple-800 rounded-xl font-extrabold"
    >
      Export ALL
    </button>
  </div>
);
}
